import { module } from 'modujs'
import { clamp, map } from '../utils/maths'
import { html } from '../utils/environment'
import { gsap, CustomEase } from 'gsap/all'

gsap.registerPlugin(CustomEase)
CustomEase.create('custom', '0.38, 0.005, 0.215, 1')

const DRAG_THRESHOLD = 10
const DRAG_LERP = .1

export default class extends module {
    constructor(m) {
        super(m)

        // Binding
        this.onResizeBind = this.onResize.bind(this)
        this.onFontsLoadedBind = this.onFontsLoaded.bind(this)
        this.onDragStartBind = this.onDragStart.bind(this)
        this.onDragEndBind = this.onDragEnd.bind(this)
        this.onDragBind = this.onDrag.bind(this)
        this.onUpdateBind = this.onUpdate.bind(this)

        // UI
        this.$el = this.el
        this.$listItems = this.$('listItem')
        this.$listItemsButton = this.$('listItemButton')
        this.$visuals = this.$('visual')
        this.$dummyAreas = this.$('dummyArea')
        this.$mobileCarousel = this.$('mobileCarousel')[0]

        // Data
        this.itemsCount = Array.from(this.$listItems).length
        this.isAnimating = false

        this.data = {
            oldIndex: 0,
            currentIndex: 0,
            oldProgress: 0,
            normalizedProgress: 0
        }

        // Metrics
        this.metrics = {
            heights: [],
            offsets: []
        }

        // Drag
        this.drag = {
            startX: 0,
            lastX: 0,
            distX: 0,
            oldX: 0,
            x: 0,
            smoothX: 0,
            lerp: DRAG_LERP,
            isStarted: false
        }

        // Drag threshold
        this.dragThreshold = DRAG_THRESHOLD

        // Slides
        this.slideWidth = 0

        // Events
        this.events = {
            click: {
                listItemButton: 'onListItemButtonClick'
            }
        }
    }

    ///////////////
    // Lifecyle
    ///////////////
    init() {
        this.bindEvents()

        if (window.isFontsLoaded) {
            this.onFontsLoaded()
        }
    }

    destroy() {
        super.destroy()
        this.unbindEvents()
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        window.addEventListener("resizeEnd", this.onResizeBind)
        window.addEventListener("fontsLoaded", this.onFontsLoadedBind)

        // Desktop
        this.$mobileCarousel.addEventListener("mousedown",  this.onDragStartBind)
        this.$mobileCarousel.addEventListener("mouseup",    this.onDragEndBind)
        this.$mobileCarousel.addEventListener("mouseleave", this.onDragEndBind)
        this.$mobileCarousel.addEventListener("mousemove",  this.onDragBind)

        // Mobile
        this.$mobileCarousel.addEventListener("touchstart", this.onDragStartBind, { passive: true })
        this.$mobileCarousel.addEventListener("touchend",   this.onDragEndBind, { passive: true })
        this.$mobileCarousel.addEventListener("touchleave", this.onDragEndBind, { passive: true })
        this.$mobileCarousel.addEventListener("touchmove",  this.onDragBind, { passive: true })
    }

    unbindEvents() {
        window.removeEventListener("resizeEnd", this.onResizeBind)
        window.removeEventListener("fontsLoaded", this.onFontsLoadedBind)

        // Desktop
        this.$mobileCarousel.removeEventListener("mousedown", this.onDragStartBind)
        this.$mobileCarousel.removeEventListener("mouseup", this.onDragEndBind)
        this.$mobileCarousel.removeEventListener("mouseleave", this.onDragEndBind)
        this.$mobileCarousel.removeEventListener("mousemove", this.onDragBind)

        // Mobile
        this.$mobileCarousel.removeEventListener("touchstart", this.onDragStartBind, { passive: true })
        this.$mobileCarousel.removeEventListener("touchend", this.onDragEndBind, { passive: true })
        this.$mobileCarousel.removeEventListener("touchleave", this.onDragEndBind, { passive: true })
        this.$mobileCarousel.removeEventListener("touchmove", this.onDragBind, { passive: true })
    }

    ///////////////
    // Callbacks
    ///////////////
    onResize() {
        this.computeMetrics()
        this.computeActiveItem()
        this.isMobile = window.innerWidth < 1000 ? true : false
        
        if (window.innerWidth < 1000) {
            this.initCarousel()
        }
    }

    onFontsLoaded() {
        this.computeMetrics()
        this.computeActiveItem()
        this.isMobile = window.innerWidth < 1000 ? true : false
        
        if (window.innerWidth < 1000) {
            this.initCarousel()
        }
    }

    onScroll(progress) {
        if (this.isMobile) return
        // calc normalized progress
        this.data.normalizedProgress = clamp(
            map(progress, 0, 1, 0, this.itemsCount),
            0,
            this.itemsCount
        )

        // get current index
        this.data.currentIndex = Math.floor(this.data.normalizedProgress)

        // if index changed
        if (this.data.currentIndex != this.data.oldIndex) {
            this.computeActiveItem()
        }
    }

    onListItemButtonClick(e) {
        const selectedElement = e.currentTarget
        const selectedIndex = Array.from(this.$listItemsButton).indexOf(selectedElement)

        if (this.data.currentIndex != selectedIndex) {
            this.data.currentIndex = selectedIndex
            const selectedArea = this.$dummyAreas[selectedIndex]
            this.call('scrollTo', { target: selectedArea, options: { duration: 0, offset: 2, disableLerp: true } }, 'Scroll', 'main')
        }
    }

    onDragStart(event) {
        if (this.isAnimating || !this.isMobile) return

        // Add draging class
        html.classList.add('is-dragging')

        const pointerEvent = event.type !== 'mousedown' ? (event.touches[0] || event.changedTouches[0]) : event;

        // Set initial drag value
        this.drag.isStarted = true
        this.drag.oldX = this.drag.x
        this.drag.startX = pointerEvent.clientX

        this.play()
    }

    onDrag(event) {
        if (!this.drag.isStarted) return

        const pointerEvent = event.type !== 'mousemove' ? (event.touches[0] || event.changedTouches[0]) : event

        // Get current drag value
        this.drag.x = this.drag.oldX + (pointerEvent.clientX - this.drag.startX)
        this.drag.lastX = pointerEvent.clientX
    }

    onDragEnd(event) {
        if (!this.drag.isStarted) return;
        
        // Set ending drag value
        this.drag.isStarted = false;

        // Remove draging class
        html.classList.remove('is-dragging')

        // Get distance between start and end
        this.distX = this.drag.lastX - this.drag.startX

        // Snap to next / prev position
        if (this.distX > this.dragThreshold) {
            this.goToPrevSlide()
        } else if (this.distX < this.dragThreshold * -1) {
            this.goToNextSlide()
        } else {
            this.goToCurrentSlide()
        }
    }

    onUpdate() {
        this.drag.smoothX += (this.drag.x - this.drag.smoothX) * this.drag.lerp
        this.drag.smoothX = ((100 * (this.drag.smoothX + 0.01)) | 0) / 100
        this.computeProgress(this.drag.smoothX)

        if (Math.round(this.drag.smoothX) === Math.round(this.drag.x) && !this.drag.isStarted) {
            this.rafIncrement++
            if (this.rafIncrement >= 30) {
                this.pause()
            }
        }        
    }

    ///////////////
    // Methods
    ///////////////
    computeActiveItem() {
        // Deactivate all list items
        for (let index = 0; index < this.$listItems.length; index++) {
            const $item = this.$listItems[index]
            $item.classList.remove('is-active')
        }

        this.$listItems[this.data.currentIndex].classList.add('is-active')

        // Deactivate all visual items
        for (let index = 0; index < this.$visuals.length; index++) {
            const $visual = this.$visuals[index]
            $visual.classList.remove('is-active')
        }

        this.$visuals[this.data.currentIndex].classList.add('is-active')

        this.$el.style.setProperty('--current-text-height', this.metrics.heights[this.data.currentIndex] + 'px')
        this.$el.style.setProperty('--current-text-offset', this.metrics.offsets[this.data.currentIndex] + 'px')

        this.data.oldIndex = this.data.currentIndex
    }

    computeMetrics() {
        let index = 0
        let cumulativeOffset = 0
        while (index < this.itemsCount) {
            const height = this.$listItemsButton[index].clientHeight
            if (index != 0) cumulativeOffset += this.$listItems[index - 1].clientHeight
            
            this.metrics.heights[index] = height
            this.metrics.offsets[index] = cumulativeOffset
            
            index++
        }
    }

    // Carousel
    ///////////////
    initCarousel() {
        // Store slide width
        this.slideWidth = this.$listItems[0].offsetWidth

        // this.onUpdate()
    }

    goToPrevSlide() {
        if (this.data.currentIndex > 0) {
            this.data.currentIndex = this.data.currentIndex - 1
        }

        this.setTranslate()
        this.computeActiveItem()
    }

    goToNextSlide() {
        if (!this.$listItems) return
        
        if (this.data.currentIndex < this.itemsCount - 1) {
            this.data.currentIndex = this.data.currentIndex + 1
        }

        this.setTranslate()
        this.computeActiveItem()
    }

    goToCurrentSlide() {
        this.setTranslate()
    }

    setTranslate() {
        this.drag.x = this.slideWidth * this.data.currentIndex * - 1
    }

    computeProgress(x) {
        const clampedX = clamp(x, -this.slideWidth * (this.itemsCount - 1), 0)
        this.$el.style.setProperty("--translate-x", clampedX + 'px')
    }

    // RAF
    ///////////////
    play() {
        if (this.isRafPlaying) return

        this.isRafPlaying = true
        this.rafIncrement = 0

        gsap.ticker.add(this.onUpdateBind)
    }

    pause() {
        if (!this.isRafPlaying) return

        this.isRafPlaying = false

        gsap.ticker.remove(this.onUpdateBind)
    }
}